import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, currentWeek } from "@/lib/format";
import { WeekSelect } from "@/components/WeekSelect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Lock, Unlock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Reconciliation() {
  const { settings, isAdmin } = useAuth();
  const cur = settings.currency || "£";
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState("");
  const [week, setWeek] = useState(currentWeek());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/sites").then((r) => { setSites(r.data); if (r.data[0]) setSite(r.data[0].id); }); }, []);
  const load = () => { if (!site) return; setData(null); api.get(`/reconciliation?site_id=${site}&week=${week}`).then((r) => setData(r.data)); };
  useEffect(() => { load(); }, [site, week]);

  const doAction = async (action) => {
    setBusy(true);
    try {
      await api.post(`/reconciliation/${action}?site_id=${site}&week=${week}`);
      toast.success(action === "close" ? "Week reconciled and closed" : "Week reopened");
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-head text-2xl lg:text-3xl font-semibold text-slate-900">Weekly Close / Reconciliation</h1>
        <p className="text-slate-500 text-sm mt-1">Reconcile and lock a financial period. Closed weeks cannot be edited by staff.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={site} onValueChange={setSite}>
          <SelectTrigger data-testid="recon-site" className="w-[200px] bg-white"><SelectValue placeholder="Home" /></SelectTrigger>
          <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <WeekSelect value={week} onChange={setWeek} />
      </div>

      {!data ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <Card className="border-border p-6 space-y-5" data-testid="recon-summary">
          <div className="flex items-center justify-between">
            <div><h2 className="font-head text-xl font-medium text-slate-800">{data.site_name}</h2><p className="text-sm text-slate-500">{data.week_label}</p></div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${data.status === "reconciled" ? "bg-slate-200 text-slate-700" : "bg-emerald-50 text-emerald-700"}`}>
              {data.status === "reconciled" ? <><Lock className="h-3.5 w-3.5" /> Reconciled</> : <><Unlock className="h-3.5 w-3.5" /> Open</>}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Line label="Opening Allocation" value={money(data.allocation, cur)} />
            <Line label="Approved Spend" value={money(data.spent, cur)} />
            <Line label="Remaining" value={money(data.remaining, cur)} accent="text-emerald-700" />
            <Line label="Transactions" value={data.transactions} />
            <Line label="Receipts Received" value={data.receipts} />
            <Line label="Missing Receipts" value={data.missing_receipts} accent={data.missing_receipts ? "text-red-600" : ""} />
            <Line label="Outstanding Queries" value={data.outstanding_queries} accent={data.outstanding_queries ? "text-amber-600" : ""} />
            <Line label="Adjustments" value={data.adjustments?.length || 0} />
          </div>

          {data.status !== "reconciled" ? (
            <div className="pt-3 border-t border-border">
              {(data.outstanding_queries > 0) && <p className="text-sm text-amber-600 mb-3">Resolve outstanding queries and pending approvals before closing.</p>}
              <Button data-testid="close-week-btn" disabled={busy} onClick={() => doAction("close")} className="bg-[#0F172A] hover:bg-[#1E293B]">
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Reconcile & Close Week
              </Button>
            </div>
          ) : (
            <div className="pt-3 border-t border-border flex items-center justify-between">
              <p className="text-sm text-slate-500">This week is locked. Only a System Administrator can reopen it (audited).</p>
              {isAdmin && <Button data-testid="reopen-week-btn" variant="outline" disabled={busy} onClick={() => doAction("reopen")} className="border-red-300 text-red-600 hover:bg-red-50"><Unlock className="h-4 w-4 mr-1.5" /> Reopen</Button>}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

const Line = ({ label, value, accent }) => (
  <div className="rounded-lg bg-slate-50 border border-border p-3">
    <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">{label}</div>
    <div className={`font-mono tabular-nums text-lg font-semibold mt-1 ${accent || "text-slate-900"}`}>{value}</div>
  </div>
);
